import { Close } from '@mui/icons-material';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { updateArrearRate } from 'lib/api/document';
import { getAvailableArrearTables } from 'lib/api/factoring';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import { showAlert } from 'lib/functions/utils';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type EditArrearDocument = {
  documentId: string;
  arrearStructure: {
    arrearType: string;
    arrearRate: number;
    arrearSegments: {
      min: number;
      max: number;
      value: number;
    }[];
  };
  hasConciliations: boolean;
} | null;

interface EditArrearDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  document: EditArrearDocument;
  onSave: () => void;
  conciliationDate: Date;
}

interface ArrearTable {
  id: number;
  name: string;
  arrearSegments: {
    min: number;
    max: number;
    value: number;
  }[];
}

const EditArrearDialog = (props: EditArrearDialogProps) => {
  const { open, setOpen, document, onSave, conciliationDate } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [availableArrearTables, setAvailableArrearTables] = useState<
    ArrearTable[]
  >([]);
  const [arrearType, setArrearType] = useState<'lineal' | 'table'>('lineal');
  const [arrearRate, setArrearRate] = useState<string>('');
  const [selectedTableId, setSelectedTableId] = useState<number | null>(null);
  const [arrearSegments, setArrearSegments] = useState<
    { min: number; max: number; value: number }[]
  >([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    setArrearType('lineal');
    setArrearRate('');
    setArrearSegments([]);
    setSelectedTableId(null);
  }, [document]);

  const loadArrearTables = async () => {
    setLoading(true);
    try {
      const tables = await getAvailableArrearTables(dispatch);
      setAvailableArrearTables(tables || []);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al obtener las tablas de mora');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (open) {
      void loadArrearTables();
    }
  }, [open]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (arrearType === 'lineal') {
      if (!arrearRate) {
        newErrors.arrearRate = 'El porcentaje es requerido';
      }
      const rateValue = Number(arrearRate);
      if (isNaN(rateValue) || rateValue < 0) {
        newErrors.arrearRate = 'El porcentaje debe ser mayor o igual a 0';
      }
    } else if (arrearType === 'table') {
      if (!selectedTableId) {
        newErrors.selectedTable = 'Debe seleccionar una tabla';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleArrearTypeChange = (type: 'lineal' | 'table') => {
    setArrearType(type);
    setErrors({});
    if (type === 'lineal') {
      setSelectedTableId(null);
    }
  };

  const handleTableSelection = (tableId: number | null) => {
    setSelectedTableId(tableId);
    setErrors({});
    if (tableId) {
      const selectedTable = availableArrearTables.find((t) => t.id === tableId);
      if (selectedTable) {
        setArrearSegments(selectedTable.arrearSegments);
      }
    }
  };

  const handleSave = async () => {
    if (!validateForm() || !document) return;

    setSaving(true);
    try {
      const payload: {
        documentId: string;
        arrearType: string;
        arrearRate: number;
        arrearTableId?: number | null;
        hasConciliations: boolean;
        conciliationDate: Date;
      } = {
        documentId: document.documentId,
        arrearType,
        arrearRate: arrearType === 'lineal' ? Number(arrearRate) || 0 : 0,
        hasConciliations: document.hasConciliations,
        conciliationDate: conciliationDate,
      };

      if (arrearType === 'table') {
        payload.arrearTableId = selectedTableId;
      }

      await updateArrearRate(dispatch, payload);

      showAlert(dispatch, 'success', 'Tasa de mora actualizada correctamente');
      onSave();
      setOpen(false);
    } catch (error) {
      showAlert(dispatch, 'error', 'Error al actualizar la tasa de mora');
    }
    setSaving(false);
  };

  const handleCancel = () => {
    setOpen(false);
    setErrors({});
    setArrearType('lineal');
    setArrearRate('');
    setSelectedTableId(null);
  };

  if (loading || saving) {
    return (
      <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">Editar tasa de mora</Typography>
            <IconButton onClick={handleCancel}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <div style={{ minHeight: 150 }}>
            <CenteredCircularProgress type="dialog" />
          </div>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <CustomButton
              onClick={handleCancel}
              disabled={saving}
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton
              color="primary"
              onClick={() => void handleSave()}
              disabled={loading || saving}
            >
              Guardar
            </CustomButton>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Editar tasa de mora</Typography>
          <IconButton onClick={handleCancel}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
          <Typography sx={{ mb: 1 }}>
            En caso de haber conciliaciones pasadas, estas no se verán afectadas
            por la nueva mora. Se aplicará una tabla de mora personalizada para
            el documento en caso de ser necesario.
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Tipo de mora</InputLabel>
            <Select
              value={arrearType}
              onChange={(e) =>
                handleArrearTypeChange(e.target.value as 'lineal' | 'table')
              }
              label="Tipo de mora"
              disabled={document?.hasConciliations}
            >
              <MenuItem value="lineal">Lineal</MenuItem>
              <MenuItem value="table">Tabla</MenuItem>
            </Select>
          </FormControl>

          {arrearType === 'lineal' && (
            <TextField
              label="Porcentaje de mora (%)"
              type="number"
              inputProps={{ step: '0.01' }}
              value={arrearRate}
              onChange={(e) => setArrearRate(e.target.value)}
              error={!!errors.arrearRate}
              helperText={
                errors.arrearRate ||
                'Ingrese el porcentaje de mora (mayor o igual a 0)'
              }
              placeholder="0"
              fullWidth
            />
          )}

          {arrearType === 'table' && (
            <Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>Tabla existente</InputLabel>
                  <Select
                    value={selectedTableId || ''}
                    onChange={(e) =>
                      handleTableSelection(e.target.value as number)
                    }
                    label="Tabla existente"
                  >
                    {availableArrearTables.map((table) => (
                      <MenuItem key={table.id} value={table.id}>
                        {table.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {selectedTableId && (
                <Box>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Vista previa de la tabla seleccionada:
                  </Typography>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tramo</TableCell>
                          <TableCell>Valor (%)</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {arrearSegments.map((segment, index) => {
                          const isLast = index === arrearSegments.length - 1;
                          const segmentName = isLast
                            ? `Más de ${segment.min} días`
                            : `${segment.min} a ${segment.max} días`;
                          return (
                            <TableRow key={index}>
                              <TableCell>{segmentName}</TableCell>
                              <TableCell>{segment.value}%</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <CustomButton
              onClick={handleCancel}
              disabled={saving}
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton
              color="primary"
              onClick={() => void handleSave()}
              disabled={saving || loading}
            >
              Guardar
            </CustomButton>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditArrearDialog;
